import { environment } from '../../../environments/environment';
import { IScannerSource } from '../../services/default-scanner.service';
import { Capabilities, CapabilityDetails, CapabilitySetup, DeviceConfiguration } from 'dwt/dist/types/WebTwain.Acquire';
import { DynamsoftStatic } from 'dwt/dist/types/Dynamsoft';
import { WebTwain } from 'dwt/dist/types/WebTwain';
import { DynamsoftEnumsDWT } from 'dwt/dist/types/Dynamsoft.Enum';

export interface CapabilitySetupCompatible {
  capability: DynamsoftEnumsDWT.EnumDWT_Cap | number;
  newValue: number | string | boolean;
  errorBehavior: 'fail' | 'ignore';
}

export class ScannerAPI {
  // Note: when you're editing this ScannerAPI object,
  // Hot Module Reloading won't properly reinitialize the scanner

  static readonly LOCAL_STORAGE_SOURCE_INDEX = 'localScanning:SourceIndex';
  static readonly LOCAL_STORAGE_IMG_CAPTURE_DRIVER_TYPE = 'localScanning:ImageCaptureDriverType';

  private webTwainScanner: WebTwain;
  private dynamsoftApi: (typeof DynamsoftStatic);
  private supportedImageTypesMapping: Record<string, DynamsoftEnumsDWT.EnumDWT_ImageType>;

  // tslint:disable-next-line:no-shadowed-variable
  constructor(dynamsoftApi: any, onWebTwainReady: () => void) {
    // use injected references instead of global references
    this.dynamsoftApi = dynamsoftApi;

    window['onbeforeunload'] = () => {
      // Clean up the websocket if the user leaves the page in an uncontrolled fashion.
      this.getDynamicWebTwainSubsystem().Unload();
    };

    /* If ContainerMap keys exist RegisterEvent has already been fired */
    const hasControlContainer = !!Object.keys(this.getDynamicWebTwainSubsystem().ContainerMap || {}).length;

    if (!hasControlContainer) {
      this.getDynamicWebTwainSubsystem().RegisterEvent('OnWebTwainReady', () => {
        onWebTwainReady();
      });
    }

    this.initImageTypes();

    if (hasControlContainer) {
      onWebTwainReady();
    }

    this.configureDynamsoft();
  }

  public async initializeScanner(): Promise<boolean> {
    console.log('INFO: Initializing scanner.');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return new Promise((resolve, reject) => {
      // The #dwtcontrolContainer is turned into a scanner object by the dwt library
      // before now; here we just ask the library for a reference to it.
      // To change the name of the container or add additional containers,
      // you'd have to edit Resources/dynamsoft.webtwain.config.js
      this.webTwainScanner = this.getDynamicWebTwainSubsystem().GetWebTwain('dwtcontrolContainer');

      if (this.webTwainScanner) {
        /* istanbul ignore else  */
        // just so we can play with the API in the Chrome DevTools
        window['DRC_scanner'] = this.webTwainScanner;

        this.configureScannerBeforeOpeningConnection();
        resolve(true);
      } else {
        throw new Error('The scanner failed to initialize.');
      }
    });
  }

  configureDynamsoft() {
    // Can also use dynamsoft.webtwain.config.js to globally configure some of Dynamsoft's settings.

    // This might be useful but, I haven't yet found it so.
    // this.dynamsoftApi.Lib.debug = !environment.production;

    // Hide the 'circle of orange spheres' progress indicator. This seemed to be the only way that this worked reliably, i.e. setting flags
    // in dynamsoft.webtwain.config.js and / or overriding OnWebTwainPreExecute did not seem to work.
    this.dynamsoftApi.Lib.showMask = this.dynamsoftApi.Lib.hideMask;
    this.dynamsoftApi.Lib.hideMask();
  }

  getScanningResolution() {
    // Every scanner these days should handle, at least 300 DPI. Below 200 the scans are poor quality.
    // TODO increase scanning resolution / DPI?
    const MIN_DPI = 150;
    // The intent of this is to use the scanner's default resolution as long as it is above our MIN_DPI.
    // Higher is better but, also produces more data.
    // I know if Dynamsoft supports this anymore since they introduced getCapabilities() & setCapabilities().
    return (this.webTwainScanner.Resolution) ? Math.max(this.webTwainScanner.Resolution, MIN_DPI) : MIN_DPI;
  }

  getAcquireOptions(): DeviceConfiguration {
    if (environment.production) {
      return {
        Resolution: this.getScanningResolution(),
      };
    }
    return {
      // Retrieve image information.
      IfGetImageInfo: true,
      IfGetExtImageInfo: true,
      Resolution: this.getScanningResolution(),
    };
  }

  public async acquireImages(): Promise<any> {
    if (this.webTwainScanner && !this.webTwainScanner.CurrentSourceName) {
      throw this.makeScannerError('Scanner is not initialized or a source has not been selected.');
    }

    return this.checkFeederForDocuments()
      .then(() => this.openScannerConnection())
      .then((opened) => {
        if (opened) {
          console.log(`Scanner source opened with result: ${opened}.`);
          return this.configureScannerAfterOpeningConnection();
        }
        throw this.makeScannerError('opening scanner connection');
      })
      .then(() => this.acquireImage());
  }

  public async extractAndClearImages(): Promise<any> {
    return this.extractImages()
      .then((blobs) => {
        this.clearImages();
        return blobs;
      });
  }

  public selectSourceByIndex(index, ImageCaptureDriverType?): boolean {
    if (ImageCaptureDriverType !== null && typeof ImageCaptureDriverType !== 'undefined') {
      localStorage.setItem(ScannerAPI.LOCAL_STORAGE_IMG_CAPTURE_DRIVER_TYPE, ImageCaptureDriverType);
      this.webTwainScanner.ImageCaptureDriverType = ImageCaptureDriverType;
    }
    localStorage.setItem(ScannerAPI.LOCAL_STORAGE_SOURCE_INDEX, index);
    console.log(`Selecting source scanner by index`);
    return this.webTwainScanner.SelectSourceByIndex(index);
  }

  // return true if a source is selected
  public checkScannerSource() {
    if (!this.webTwainScanner.CurrentSourceName) {
      // check for last selected in local storage
      const sourceIndex = localStorage.getItem(ScannerAPI.LOCAL_STORAGE_SOURCE_INDEX);
      const imageCaptureDriverType = localStorage.getItem(ScannerAPI.LOCAL_STORAGE_IMG_CAPTURE_DRIVER_TYPE);
      if (sourceIndex) {
        return this.selectSourceByIndex(sourceIndex, imageCaptureDriverType);
      } 
      return false;
      
    }
    return true;
  }

  public async acquireImage(): Promise<any> {
    return new Promise((resolve, reject) => {
      // passing a failureCallBack here to handle sending both errorCode and errorString in the reject
      return this.webTwainScanner.AcquireImage(this.getAcquireOptions(), resolve as any,
        (deviceConfiguration, errorCode: number, errorString: string) => {
          const e = {
            errorCode: errorCode,
            message: errorString,
          };
          reject(e);
        });
    });
  }

  public async extractImages(): Promise<any[]> {
    const count = this.webTwainScanner.HowManyImagesInBuffer;
    const imageBlobPromiseFactories = [];

    for (let i = 0; i < count; i++) {
      imageBlobPromiseFactories.push(this.extractImage(i));
    }

    return imageBlobPromiseFactories.reduce((promise, getBlob) => {
      return promise.then((blobs) => {
        return getBlob().then(Array.prototype.concat.bind(blobs));
      });
    }, Promise.resolve([]));
  }

  public clearImages(): boolean {
    return this.webTwainScanner.RemoveAllImages();
  }

  public destroyScanner(): void {
    this.webTwainScanner = {} as WebTwain;
    this.getDynamicWebTwainSubsystem().DeleteDWTObject('dwtcontrolContainer');
  }

  isMacPlatform() {
    return navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  }

  getDynamicWebTwainSubsystem(): (typeof DynamsoftStatic.DWT) {
    return this.dynamsoftApi.DWT;
  }

  initImageTypes(): void {
    // EnumDWT_ImageType is a global provided by the Dynamsoft dwt sdk
    const ImageTypeEnum = this.getDynamicWebTwainSubsystem().EnumDWT_ImageType || {} as any;

    /* istanbul ignore next */
    if (ImageTypeEnum.IT_PNG === undefined) {
      console.log('Error: EnumDWT_ImageType is not available on window;' +
        ' the Dynamsoft Dynamic Web TWAIN SDK may be missing.');
    }

    this.supportedImageTypesMapping = {
      BMP: ImageTypeEnum.IT_BMP,
      JPG: ImageTypeEnum.IT_JPG,
      TIF: ImageTypeEnum.IT_TIF,
      PNG: ImageTypeEnum.IT_PNG,
      PDF: ImageTypeEnum.IT_PDF,
    };
  }

  configureScannerBeforeOpeningConnection(): void {
    // Return or set whether to close the user interface after all images have been acquired. This property only makes sense
    // when IfShowUI is set to true.
    // http://weblogos.du.edu.om/appsimages/javascript/scan/Documents/Help/Properties/prop%20IfDisableSourceAfterAcquire.htm
    this.webTwainScanner.IfDisableSourceAfterAcquire = true;
    // Don't let them customize their settings once they select a source
    this.webTwainScanner.IfShowUI = false;
  }

  async configureScannerAfterOpeningConnection(): Promise<any> {
    // Some configuration options are required to be set after
    // calling the Dynamsoft OpenSource() function.

    console.log(`Configuring scanner driver properties after opening...`);

    this.webTwainScanner.PageSize = this.getDynamicWebTwainSubsystem().EnumDWT_CapSupportedSizes.TWSS_USLETTER;
    this.webTwainScanner.PixelType = this.getDynamicWebTwainSubsystem().EnumDWT_PixelType.TWPT_GRAY;
    this.webTwainScanner.Resolution = this.getScanningResolution();

    // Don't display the scanner manufacturer's UI allowing them to customize their settings once they select a source.
    this.webTwainScanner.IfShowUI = false;
    // Hide the Progress Bar display during saving, loading or converting. It works for any image encoding/decoding related
    // methods. For example: LoadImage, LoadImageEx, ConvertToBlob, etc.
    this.webTwainScanner.IfShowProgressBar = false;
    // Hide the Progress Bar when uploading scan images.
    this.webTwainScanner.IfShowCancelDialogWhenImageTransfer = false;

    console.log(`INFO: Configuring driver capabilities...`);
    // Setup all the capabilities on the scanner, as a promise chain. This ensures the capabilities are set before than scanning starts.
    // If these are set during the scanning process, errors will result.
    return this.setDuplex()
      .then(() => {
        return this.alterLightSourceIfInfrared();
      });
  }

  formatCapabilityDetail(capDetail: CapabilityDetails) {
    if (capDetail && capDetail.capability.value) {
      return `Capability ` +
        `${capDetail.capability.label} (${capDetail.capability.value} | 0x${capDetail.capability.value.toString(16)}): ` +
        `value: ${JSON.stringify(capDetail.curValue)}, type: ${capDetail.valueType.label}`;
    }
  }

  async setDuplex(): Promise<any> {
    // Check if it is successfully set...
    let feederEnabledCapDef = await this.getCapability(this.getDynamicWebTwainSubsystem().EnumDWT_Cap.CAP_FEEDERENABLED);
    if (!feederEnabledCapDef?.curValue?.value) {
      await this.setCapabilities([{
        // Enable multi-page feeding, if available
        capability: this.getDynamicWebTwainSubsystem().EnumDWT_Cap.CAP_FEEDERENABLED,
        newValue: true,
        errorBehavior: 'ignore',
      }]);
    }

    // Check if it is set as we want...
    feederEnabledCapDef = await this.getCapability(this.getDynamicWebTwainSubsystem().EnumDWT_Cap.CAP_FEEDERENABLED);
    let duplexCapDef = null;
    if (feederEnabledCapDef?.curValue?.value) {
      // Now we can check if duplex is supported.
      duplexCapDef = await this.getCapability(this.getDynamicWebTwainSubsystem().EnumDWT_Cap.CAP_DUPLEX);
      if (duplexCapDef?.curValue?.value > 0) {
        await this.setCapabilities([{
          // Enable duplex scanning, if available
          capability: this.getDynamicWebTwainSubsystem().EnumDWT_Cap.CAP_DUPLEXENABLED,
          newValue: true,
          errorBehavior: 'ignore',
        }]);
      }
    }
    console.log(`When setting duplex: ${this.formatCapabilityDetail(feederEnabledCapDef)}`);
    console.log(`When setting duplex: ${this.formatCapabilityDetail(duplexCapDef)}`);
  }

  /**
   * If the user has an infrared scanner, set the light source to red. Returns true if the scanner is infrared, otherwise
   * false.
   */
  async alterLightSourceIfInfrared(): Promise<boolean> {
    console.log('Checking if IR light source used');
    const dwt = this.getDynamicWebTwainSubsystem();
    return this.getCapability(dwt.EnumDWT_Cap.ICAP_LIGHTSOURCE)
      .then((isInfraredCapability: CapabilityDetails) => {
        if (isInfraredCapability?.curValue?.value === dwt.EnumDWT_CapLightSource.TWLS_IR) {
          console.log('INFO: IR light source used, changing to red');
          return this.setCapabilities([
            {
              capability: dwt.EnumDWT_Cap.ICAP_LIGHTSOURCE,
              newValue: dwt.EnumDWT_CapLightSource.TWLS_RED,
              errorBehavior: 'ignore',
            }
          ]).then(() => true);
        }
        console.log('INFO: IR light source not being used');
        return false;
      });
  }

  async getCapability(capabilityId: number | string): Promise<CapabilityDetails> {
    return new Promise((resolve, reject) => {
      this.webTwainScanner.getCapabilities((capabilityDetails: CapabilityDetails[]) => {
        const details = capabilityDetails.find((capDet) => capDet.capability.value === capabilityId);
        resolve(details);
      }, () => {
        reject(this.makeScannerError('when checking scanner capabilities'));
      });
    });
  }

  mapCapabilityInteractionsToSetups(capabilityInteractions: CapabilitySetupCompatible[]): CapabilitySetup[] {
    return capabilityInteractions.map((ci) => {
      return {
        capability: ci.capability,
        curValue: ci.newValue,
        exception: ci.errorBehavior
      } as CapabilitySetup;
    });
  }

  async setCapabilities(capabilitySetups: CapabilitySetupCompatible[]): Promise<Capabilities> {
    // NOTE this is mapping our own 'newValue' to Dynamsoft's 'curValue' because their Typescript typings define it as
    // only taking either a number or string. The TWAIN spec. says it can also be a boolean.
    const errorHandling = new Set(capabilitySetups.map((el) => el.errorBehavior));
    if (errorHandling.size > 1) {
      console.log('WARN: All Capabilities set at one time will have the same error handling behavior.');
    }

    const capSetups = this.mapCapabilityInteractionsToSetups(capabilitySetups);
    return new Promise((resolve, reject) => {
      this.webTwainScanner.setCapabilities(
        {
          // Dynamsoft has a typo in their typings or API. This sets both assuming it will ignore extra properties and we will
          // not have to worry about what the default value is when/if they fix it and we do not catch immediately.
          exceptition: capabilitySetups[0].errorBehavior,
          exception: capabilitySetups[0].errorBehavior,
          capabilities: capSetups,
        } as Capabilities,
        function (capabilities) {
          console.log('INFO: Capabilities setting results:', JSON.stringify(capabilities.capabilities));
          resolve(capabilities);
        },
        function (capabilities) {
          console.warn('WARNING: Capabilities NOT set:', JSON.stringify(capabilities.capabilities));
          reject(new Error(`Failed to set scanner capabilities!`));
        }
      );
    });
  }

  openScannerConnection(): boolean {
    console.log('INFO: Opening source scanner.');
    return this.webTwainScanner.OpenSource();
  }

  // Should be marked as private, but needs to be public for mocking in tests
  async getSourceList(): Promise<IScannerSource[]> {
    let sources: IScannerSource[] = [];
    const isMac = this.isMacPlatform();

    if (!isMac) {
      sources = this.readSourceList();
    } else {
      // MAC OS ONLY: Use ICA drivers, support USB or Networked Scanners
      this.webTwainScanner.ImageCaptureDriverType = 3;
      sources.push(...this.readSourceList().map((source) => ({
        ...source,
        ImageCaptureDriverType: 3,
        driverTypeName: 'Apple ICA',
      })));

      // MAC OS ONLY: Use TWAIN drivers
      this.webTwainScanner.ImageCaptureDriverType = 0;
      sources.push(...this.readSourceList().map((source) => ({
        ...source,
        ImageCaptureDriverType: 0,
        driverTypeName: 'TWAIN',
      })));
    }

    return Promise.resolve(sources);
  }

  readSourceList(): IScannerSource[] {
    const sourceCount = this.webTwainScanner.SourceCount;
    const sources: IScannerSource[] = [];

    for (let i = 0; i < sourceCount; i++) {
      const sourceName = this.webTwainScanner.GetSourceNameItems(i);
      if (this.isSourceValid(sourceName)) {
        sources.push({index: i, name: sourceName});
      }
    }

    return sources;
  }

  isSourceValid(sourceName: string): boolean {
    const restrictedSources = [
      'WIA',
      'camera',
      'webcam',
    ];

    let valid = true;

    restrictedSources.forEach((restriction) => {
      // sourceName doesn't contain the restriction
      if (sourceName.toLowerCase().indexOf(restriction.toLowerCase()) !== -1) {
        valid = false;
      }
    });

    return valid;
  }

  async checkFeederForDocuments(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (!this.webTwainScanner) {
        return reject(this.makeScannerError('when checking feeder for documents'));
      }

      // TODO These should be converted to getCapabilities() checks performed after OpenSource() is called.
      //    CAP_FEEDERENABLED, CAP_PAPERDETECTABLE, CAP_FEEDERLOADED
      if (this.webTwainScanner.IfFeederEnabled && this.webTwainScanner.IfPaperDetectable) {
        // If feeder can detect paper, respond with whether there is any.
        if (this.webTwainScanner.IfFeederLoaded) {
          return resolve(this.webTwainScanner.IfFeederLoaded);
        } 
        reject('No Documents In Feeder');
        
      } else {
        // If feeder can NOT detect paper,
        // respond with true to allow scan attempts anyway
        return resolve(true);
      }
    });
  }

  extractImage(index): () => Promise<void> {
    return () => {
      return new Promise((resolve, reject) => {
        this.webTwainScanner.ConvertToBlob([index], this.supportedImageTypesMapping.PNG, resolve as any, reject);
      });
    };
  }

  makeScannerError(messageDescribingContextOfWhenCalled: string): Error {
    let message = `ScannerAPI: Error communicating with scanner `;
    if (messageDescribingContextOfWhenCalled) {
      message += messageDescribingContextOfWhenCalled;
    }
    message += '.';
    if (this.webTwainScanner.ErrorCode) {
      message += ` ${this.webTwainScanner.ErrorString}`;
    }
    console.log(`${message} Dynamic Web TWAIN: error code ${this.webTwainScanner.ErrorCode}`);
    return new Error(message);
  }

}
